"use client";

import React, { createContext, useContext } from "react";
import type { BaseEntity } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { DetailActionContext } from "./types";

const DetailActionContextInternal = createContext<DetailActionContext | null>(null);

interface DetailActionProviderProps<T extends BaseEntity> {
  children: React.ReactNode;
  value: DetailActionContext<T>;
}

export function DetailActionProvider<T extends BaseEntity>({
  children,
  value,
}: DetailActionProviderProps<T>) {
  return (
    <DetailActionContextInternal.Provider value={value as unknown as DetailActionContext}>
      {children}
    </DetailActionContextInternal.Provider>
  );
}

export function useDetailActionContext<
  T extends BaseEntity = BaseEntity,
>(): DetailActionContext<T> {
  const context = useContext(DetailActionContextInternal);

  if (!context) {
    throw new Error("useDetailActionContext must be used within a DetailActionProvider");
  }

  return context as unknown as DetailActionContext<T>;
}

export function useDetailActionContextOptional<
  T extends BaseEntity = BaseEntity,
>(): DetailActionContext<T> | null {
  const context = useContext(DetailActionContextInternal);
  return context as unknown as DetailActionContext<T> | null;
}
